import React, { useState, useEffect } from "react";
import { Form, Input, Select, Checkbox, message, Space, Modal, Button, Table } from "antd";
import { UI_CONTROLS } from "../../Types/FieldTypes";
import { WebApiModelsCustomListField } from "../../Repository/eventbookyapi";
import { CustomListFieldService } from "../../Services/CustomLifFieldService";
import { SubscriptionLevel } from "../../Types/SubscriptionLevel";

const { Option } = Select;

interface CustomFieldTableProps {
    customList: any;
    customListFields: WebApiModelsCustomListField[];
    isMemberList: boolean;
    allCustomLists: any[];
    onSchemaChange?: (fields: WebApiModelsCustomListField[]) => void;
    onSaveSchema?: (fields: WebApiModelsCustomListField[]) => Promise<void>;
}

const CustomFieldTable: React.FC<CustomFieldTableProps> = ({ customList, customListFields, allCustomLists, onSchemaChange, onSaveSchema }) => {
    const [fields, setFields] = useState<WebApiModelsCustomListField[]>([]);
    const [loading, setLoading] = useState(false);
    const [addFieldModal, setAddFieldModal] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        setFields(customListFields);
    }, [customListFields]);

    const saveSchema = async (newRows: WebApiModelsCustomListField[]) => {
        setLoading(true);
        try {
            if (onSaveSchema) {
                await onSaveSchema(newRows);
            } else if (customList) {
                await CustomListFieldService.SaveCustomListFields({
                    listId: customList.id,
                    fields: newRows
                });
            }
            message.success("Ändringar sparade!");
        } catch (e) {
            message.error("Kunde inte spara ändringar.");
        }
        setLoading(false);
    };

    const handleFieldChange = (value: any, fieldIndex: number, key: keyof WebApiModelsCustomListField) => {
        const updated = [...fields];
        if (key === "alternatives") {
            updated[fieldIndex] = { ...updated[fieldIndex], alternatives: typeof value === "string" ? value : value.join(", ") };
        } else {
            updated[fieldIndex] = { ...updated[fieldIndex], [key]: value };
        }
        setFields(updated);
    };

    const removeField = (fieldIndex: number) => {
        const updated = fields.filter((_, i) => i !== fieldIndex);
        setFields(updated);
    };

    const onAddField = (values: any) => {
        const maxOrder = fields.length > 0 ? Math.max(...fields.map(f => f.columnOrder ?? 0)) : 0;
        const newField: WebApiModelsCustomListField = {
            columnOrder: maxOrder + 1,
            nameAdmin: values.name,
            helpTextPublic: values.label,
            lookupListId: values.lookupListId,
            uiControl: values.control,
            helpTextAdmin: values.helpTextAdmin,
            alternatives: values.options ? values.options : "",
        };
        setFields([...fields, newField]);
        setAddFieldModal(false);
        form.resetFields();
    };

    const handleOrderChange = (fieldIndex: number, newOrder: number) => {
        const updated = [...fields];
        const [moved] = updated.splice(fieldIndex, 1);
        updated.splice(newOrder - 1, 0, moved);
        updated.forEach((item, idx) => { item.columnOrder = idx + 1; });
        setFields(updated);
    };

    const columns = [
        {
            title: "Ordning",
            dataIndex: "columnOrder",
            key: "columnOrder",
            width: 100,
            render: (_: any, record: WebApiModelsCustomListField, idx: number) => (
                <Select
                    value={record.columnOrder}
                    size="small"
                    style={{ width: 70 }}
                    onChange={val => handleOrderChange(idx, val)}
                >
                    {fields.map((_, i) => (
                        <Option key={i + 1} value={i + 1}>{i + 1}</Option>
                    ))}
                </Select>
            ),
        },
        {
            title: "Namn",
            dataIndex: "nameAdmin",
            key: "nameAdmin",
            render: (_: any, record: WebApiModelsCustomListField, idx: number) => (
                <Input
                    value={record.nameAdmin ?? ""}
                    onChange={e => handleFieldChange(e.target.value, idx, "nameAdmin")}
                    size="small"
                />
            ),
        },
        {
            title: "Typ av fält",
            dataIndex: "uiControl",
            key: "uiControl",
            render: (_: any, record: WebApiModelsCustomListField, idx: number) => (
                <Select
                    value={record.uiControl}
                    onChange={v => handleFieldChange(v, idx, "uiControl")}
                    size="small"
                    style={{ width: 120 }}
                >
                    {UI_CONTROLS.map(t => (
                        <Option key={t.value} value={t.value}>{t.label}</Option>
                    ))}
                </Select>
            ),
        },
        {
            title: "Uppslag",
            dataIndex: "lookupListId",
            key: "lookupListId",
            render: (_: any, record: WebApiModelsCustomListField, idx: number) =>
                <Select
                    value={record.lookupListId}
                    onChange={v => handleFieldChange(v, idx, "lookupListId")}
                    size="small"
                    style={{ width: 160 }}
                    placeholder="Välj lista"
                >
                    {allCustomLists.map(list => (
                        <Option key={list.id} value={list.id}>{list.name}</Option>
                    ))}
                </Select>

        },
        {
            title: "Obligatorisk",
            dataIndex: "mandatoryAdmin",
            key: "mandatoryAdmin",
            render: (_: any, record: WebApiModelsCustomListField, idx: number) => (
                <Checkbox
                    checked={record.mandatoryAdmin}
                    onChange={e => handleFieldChange(e.target.checked, idx, "mandatoryAdmin")}
                />
            ),
        },
        {
            title: "Hjälptext",
            dataIndex: "helpTextAdmin",
            key: "helpTextAdmin",
            render: (_: any, record: WebApiModelsCustomListField, idx: number) => (
                <Input
                    value={record.helpTextAdmin ? record.helpTextAdmin : ""}
                    onChange={e => handleFieldChange(e.target.value, idx, "helpTextAdmin")}
                    size="small"
                />
            ),
        },
        {
            title: "Alternativ",
            dataIndex: "alternatives",
            key: "alternatives",
            render: (_: any, record: WebApiModelsCustomListField, idx: number) => (
                <Input
                    value={record.alternatives ? record.alternatives : ""}
                    onChange={e => handleFieldChange(e.target.value, idx, "alternatives")}
                    size="small"
                />
            ),
        },
        {
            title: "",
            key: "actions",
            render: (_: any, __: any, idx: number) => (
                <Button danger size="small" onClick={() => removeField(idx)}>
                    Ta bort
                </Button>
            ),
        },
    ];

    return (
        <>
            <Button type="primary" disabled={user.subscriptionLevel < SubscriptionLevel.Premium} onClick={() => setAddFieldModal(true)} style={{ marginBottom: 16 }}>
                Lägg till fält
            </Button>
            {user.subscriptionLevel < SubscriptionLevel.Premium && <span>
                <a href='/Pricing'>Uppgradera till premium</a> för att lägga fler fält</span>}
            <Table
                columns={columns}
                dataSource={fields}
                rowKey={row => row.id!}
                pagination={false}
                loading={loading}
                size="small"
            />
            <Button
                type="primary"
                onClick={() => saveSchema([...fields].sort((a, b) => (a.columnOrder ?? 0) - (b.columnOrder ?? 0)))}
                style={{ marginTop: 16 }}
                loading={loading}
            >
                Spara ändringar
            </Button>
            <Modal
                title="Lägg till nytt fält"
                open={addFieldModal}
                onCancel={() => setAddFieldModal(false)}
                footer={null}
                destroyOnClose
            >
                <Form layout="vertical" form={form} onFinish={onAddField}>
                    <Form.Item label="Namn" name="name" rules={[{ required: true, message: "Ange namn" }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item label="Typ av fält" name="control" rules={[{ required: true }]}>
                        <Select>
                            {UI_CONTROLS.map(t => (
                                <Option key={t.value} value={t.value}>{t.label}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        noStyle
                        shouldUpdate={(prev, curr) => prev.type !== curr.type}
                    >
                        {({ getFieldValue }) =>
                            getFieldValue("type") === "lookup" ? (
                                <Form.Item
                                    label="Välj lista att slå upp mot"
                                    name="lookupListId"
                                    rules={[{ required: true, message: "Välj en lista" }]}
                                >
                                    <Select placeholder="Välj lista">
                                        {allCustomLists.map(list => (
                                            <Option key={list.id} value={list.id}>{list.name}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            ) : null
                        }
                    </Form.Item>
                    <Form.Item label="Obligatorisk" name="required" valuePropName="checked">
                        <Checkbox />
                    </Form.Item>

                    <Form.Item label="Hjälptext" name="placeholder">
                        <Input />
                    </Form.Item>
                    <Form.Item label="Alternativ (semikolonseparerade, används vid Dropdown)" name="options">
                        <Input />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button htmlType="submit" type="primary">
                                Lägg till
                            </Button>
                            <Button onClick={() => setAddFieldModal(false)}>
                                Avbryt
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default CustomFieldTable;